/**
 * Helper UI + logic for the "No open fiscal period covering today" screen.
 * Extracted from SetupGuard.tsx to keep functions under the line limit.
 */

import React, { useState } from 'react'

import { Link } from 'react-router-dom'

import { BlockingCard } from './SetupGuard-shared'
import {
  createFiscalYear,
  getPeriodForDate,
  listFiscalPeriods,
} from '../services/fiscal-period-service'

const DEFAULT_FISCAL_START_MONTH = 4 // Indian FY default

interface TargetYearInput {
  todayMonth: number
  todayYear: number
  existingYears: Array<{ fiscalYear: number; startDate: string }>
}

interface TargetYearOutput {
  targetFiscalYear: number
  targetStartDate: string
  startMonth: number
}

function deriveTargetFiscalYear({ todayMonth, todayYear, existingYears }: TargetYearInput): TargetYearOutput {
  let startMonth = DEFAULT_FISCAL_START_MONTH
  if (existingYears.length > 0) {
    const latest = existingYears.reduce((a, b) => (a.fiscalYear > b.fiscalYear ? a : b))
    startMonth = parseInt(latest.startDate.split('-')[1] ?? '', 10)
  }
  const targetFiscalYear = todayMonth >= startMonth ? todayYear : todayYear - 1
  const targetStartDate = `${targetFiscalYear}-${String(startMonth).padStart(2, '0')}-01`
  return { targetFiscalYear, targetStartDate, startMonth }
}

interface CheckExistingResult {
  shouldStop: boolean
  errorMessage?: string
  isAlreadyOpen?: boolean
}

async function checkExistingPeriodForToday(orgId: string, todayStr: string): Promise<CheckExistingResult> {
  const existingForToday = await getPeriodForDate(orgId, todayStr, 'PERIOD_TYPE_YEAR')
  if (!existingForToday.data) {
    return { shouldStop: false }
  }
  if (existingForToday.data.status === 'PERIOD_STATUS_OPEN') {
    return { shouldStop: true, isAlreadyOpen: true }
  }
  return {
    shouldStop: true,
    errorMessage: 'A fiscal year covering today exists but is not open. Please reopen it in Manage Fiscal Periods.',
  }
}

interface CreateResult {
  ok: boolean
  errorMessage?: string
}

async function createCurrentFiscalYear(orgId: string): Promise<CreateResult> {
  const todayStr = new Date().toISOString().split('T')[0] ?? ''
  const today = new Date()
  const todayYear = today.getFullYear()
  const todayMonth = today.getMonth() + 1

  const existing = await checkExistingPeriodForToday(orgId, todayStr)
  if (existing.shouldStop) {
    if (existing.isAlreadyOpen === true) { return { ok: true } }
    return { ok: false, errorMessage: existing.errorMessage }
  }

  const listResp = await listFiscalPeriods(orgId, { periodType: 'PERIOD_TYPE_YEAR' }, 200)
  const existingYears = listResp.data?.periods ?? []
  const { targetFiscalYear, targetStartDate } = deriveTargetFiscalYear({ todayMonth, todayYear, existingYears })

  const hasTargetYear = existingYears.some(y => y.fiscalYear === targetFiscalYear)
  if (hasTargetYear) {
    return {
      ok: false,
      errorMessage: `Fiscal year ${targetFiscalYear} already exists but has no open period for today. Please open the relevant period in Manage Fiscal Periods.`,
    }
  }

  const resp = await createFiscalYear(orgId, {
    fiscalYear: targetFiscalYear,
    startDate: targetStartDate,
    createMonthlyPeriods: true,
    createQuarterlyPeriods: true,
    idempotencyKey: `${orgId}-fy-${targetFiscalYear}`,
  })

  if (resp.data) { return { ok: true } }
  return { ok: false, errorMessage: resp.error?.message ?? 'Failed to create fiscal year' }
}

function CreatingSpinner(): React.ReactElement {
  return (
    <>
      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" fill="currentColor" />
      </svg>
      Creating Fiscal Year…
    </>
  )
}

function CreateIcon(): React.ReactElement {
  return (
    <>
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path d="M12 6v6m0 0v6m0-6h6m-6 0H6" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
      </svg>
      Set Up Current Fiscal Year
    </>
  )
}

interface CreateButtonProps {
  isCreating: boolean
  onClick: () => void
}

function CreateFiscalYearButton({ isCreating, onClick }: CreateButtonProps): React.ReactElement {
  return (
    <button
      className="inline-flex items-center gap-2 px-6 py-3 bg-primary-solid text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed"
      disabled={isCreating}
      onClick={onClick}
    >
      {isCreating ? <CreatingSpinner /> : <CreateIcon />}
    </button>
  )
}

interface CurrentPeriodRequiredProps {
  orgId: string
  onSuccess: () => void
}

export function CurrentPeriodRequired({ orgId, onSuccess }: CurrentPeriodRequiredProps): React.ReactElement {
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleCreate(): Promise<void> {
    setIsCreating(true)
    setError(null)
    try {
      const result = await createCurrentFiscalYear(orgId)
      if (result.ok) {
        onSuccess()
      } else {
        setError(result.errorMessage ?? 'Failed to create fiscal year')
      }
    } catch {
      setError('Failed to create fiscal year')
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <BlockingCard>
      <h2 className="text-2xl font-bold text-foreground text-center mb-4">No Open Period for Today</h2>
      <p className="text-foreground-secondary text-center mb-6">There is no open fiscal period covering today&apos;s date. Click below to create the current fiscal year and open all monthly and quarterly periods.</p>
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
        <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">What will this do?</h3>
        <p className="text-sm text-blue-700 dark:text-blue-300">This will create the current fiscal year with 12 monthly periods, 4 quarterly periods, and 1 annual period — all set to Open so you can post transactions immediately.</p>
      </div>
      {error !== null && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4 text-sm text-red-700 dark:text-red-300 text-center">
          {error}
        </div>
      )}
      <div className="flex flex-col items-center gap-3">
        <CreateFiscalYearButton isCreating={isCreating} onClick={() => { void handleCreate() }} />
        <Link className="text-sm text-foreground-secondary underline underline-offset-2" to="/admin/periods">
          Manage fiscal periods manually
        </Link>
      </div>
    </BlockingCard>
  )
}
